/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


/* =================================================================
 * Quick add-to-cart "+" button with size strip
 *  - 1 variant     → POST /cart/add.js directly
 *  - 2+ variants   → open the size strip, user picks a size, then POST
 *  - On success    → refresh & open the cart drawer
 * Vanilla JS, event delegation. Hooks: .upsell-atc-btn, .variant-strip,
 *                                       .variant-strip__option
 * ================================================================= */
(function () {
  // Sections the theme keeps in sync with the cart. Asking for them in the
  // add.js POST makes Shopify render them into the same response, so one round
  // trip does the work that used to take two (add.js, then a separate
  // GET ?section_id=cart-drawer via dispatch:cart-drawer:refresh).
  function cartSectionElements() {
    return ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) {
        return el && el.dataset.sectionId && typeof el.updateFromCartChange === 'function';
      });
  }

  /**
   * Hidden line item properties recording where an add came from.
   *
   * The origin is declared in Liquid with data-upsell-source (plus an optional
   * data-upsell-from) on the surface wrapping the button — the picker card in
   * snippets/product-tag-upsell.liquid, <cc-cart-cross-sell> in
   * sections/cart-drawer.liquid — so this stays out of the business of knowing
   * which markup is an upsell. A button with no such ancestor (the "+" on a
   * collection grid) is not tagged: only upsell surfaces are measured.
   *
   * The leading underscore is what keeps the properties invisible to the
   * customer: cart-item.liquid skips every property whose name starts with one,
   * and Shopify does the same at checkout. The order line keeps them, so an add
   * made here can be told apart from the same product added anywhere else.
   *
   * The fixed cards of the PDP block don't come through here — they post a real
   * product form and write the same two properties as hidden inputs.
   */
  function upsellProperties(triggerBtn) {
    if (!triggerBtn || typeof triggerBtn.closest !== 'function') return null;
    var surface = triggerBtn.closest('[data-upsell-source]');
    if (!surface) return null;
    var source = surface.getAttribute('data-upsell-source');
    if (!source) return null;
    var props = { _lgc_src: source };
    var from = surface.getAttribute('data-upsell-from');
    if (from) props._lgc_from = from;
    return props;
  }

  // onError is optional and only used by callers that surface the failure in
  // the UI (the upsell picker). Existing two-argument callers keep the old
  // console-only behaviour.
  function addToCart(variantId, triggerBtn, onError) {
    if (!variantId) return;
    // A second click while the first add is in flight would fire a second POST
    // and a second focus handoff.
    if (triggerBtn && triggerBtn.getAttribute('aria-disabled') === 'true') return;

    if (triggerBtn) {
      triggerBtn.setAttribute('aria-disabled', 'true');
      triggerBtn.classList.add('is-loading');
    }

    var releaseBtn = function () {
      // The button may have been replaced by the section merge (a "+" on a
      // cross-sell card inside the drawer), in which case there is nothing
      // left to restore.
      if (!triggerBtn || !triggerBtn.isConnected) return;
      triggerBtn.removeAttribute('aria-disabled');
      triggerBtn.classList.remove('is-loading');
    };

    // If the click came from inside an already-open cart drawer (e.g. a "+"
    // on a cross-sell card), the upcoming HTML merge will replace that "+"
    // button and focus drops to <body>. cart-form.refreshFromHtml has a
    // recovery that pulls focus to the drawer close button in that case,
    // which makes VoiceOver announce "Cerrar, diálogo" before the real
    // "Just added to cart, Product X" message. Set the post-merge guard
    // flag so the recovery skips that hop — onMerged below will land focus
    // on the just-added line item directly.
    if (typeof theme !== 'undefined' && document.querySelector('cart-drawer[open]')) {
      theme._pendingPostMergeFocus = true;
    }

    var sectionEls = cartSectionElements();
    var payload = { id: variantId, quantity: 1 };
    var originProps = upsellProperties(triggerBtn);
    if (originProps) payload.properties = originProps;
    if (sectionEls.length) {
      payload.sections = sectionEls.map(function (el) { return el.dataset.sectionId; }).join(',');
      payload.sections_url = window.location.pathname;
    }
    var cartAddUrl = (typeof theme !== 'undefined' && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (res) {
        var data = res.data;
        // A rejected add (variant sold out, quantity limit) still returns JSON,
        // with `status` set. Opening the drawer here would show a cart that
        // never changed, so bail and let the button come back.
        if (!res.ok || !data || data.status) {
          throw new Error((data && (data.description || data.message)) || 'add rejected');
        }

        // The a11y confirmation is delivered by moving keyboard focus to the
        // newly-added cart item (which carries a hidden "Just added to cart"
        // prefix in its accessible name). See theme.focusJustAddedCartItem.
        var pendingFocusItem = null;
        var drawerWasOpen = !!document.querySelector('cart-drawer[open]');
        var hasFocusPath = data.key
          && typeof theme !== 'undefined'
          && typeof theme.focusJustAddedCartItem === 'function';

        if (hasFocusPath) {
          var onMerged = function () {
            document.removeEventListener('on:cart:after-merge', onMerged);
            theme._pendingPostMergeFocus = false;
            var itemEl = theme.focusJustAddedCartItem(data.key);
            if (!itemEl) return;
            if (drawerWasOpen) {
              // Drawer is already open; the upcoming open() dispatch will
              // no-op, so move focus to the new item directly.
              itemEl.focus();
              return;
            }
            // Drawer was closed. On the bundled-sections path this merge runs
            // before the open dispatch below, so stash the item and let the
            // drawer focus it as it opens.
            pendingFocusItem = itemEl;
            if (document.querySelector('cart-drawer[open]')) {
              // Fallback path only: the refresh was a separate request, so the
              // open dispatch already fired with no target and the drawer
              // landed focus on its default. Take over now. Defer one frame so
              // the drawer's [open] visibility styles have applied — focusing
              // while still visibility:hidden silently drops focus to <body>.
              requestAnimationFrame(function () {
                if (itemEl.isConnected) itemEl.focus();
              });
            }
          };
          document.addEventListener('on:cart:after-merge', onMerged, { once: true });
        } else if (typeof theme !== 'undefined') {
          // No after-merge focus path will run (e.g. response had no key),
          // so don't leave the guard stuck on.
          theme._pendingPostMergeFocus = false;
        }

        // If the search popup is open, close it first and capture its opener
        // (typically the search icon in the header) so the cart drawer can
        // restore focus there on close — otherwise focus is lost in a layer
        // that is no longer visible.
        var cartOpener = null;
        if (document.body.classList.contains('show-search')) {
          cartOpener = (typeof theme !== 'undefined' && theme.trapOpener)
            || document.querySelector('.show-search-link');
          var searchCloseBtn = document.querySelector('.main-search .main-search__close');
          if (searchCloseBtn) searchCloseBtn.click();
        }

        var sectionsReturned = data.sections
          && sectionEls.length
          && sectionEls.every(function (el) { return data.sections[el.dataset.sectionId]; });

        if (sectionsReturned) {
          // Synchronous — on:cart:after-merge (and onMerged) fire inside here,
          // before the drawer opens.
          sectionEls.forEach(function (el) {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          });
          if (typeof theme !== 'undefined') theme._pendingPostMergeFocus = false;
        } else {
          // Shopify didn't return the sections (unexpected null returns have
          // been observed). Fall back to the old extra round trip, which merges
          // asynchronously — onMerged clears the focus guard in that case.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        }

        // The cart page renders a featured-collection section below the cart,
        // so a "+" can be clicked there. Its line-item list is a separate
        // section from the drawer and isn't covered by the merge above.
        document.querySelectorAll('cart-form').forEach(function (cartForm) {
          if (cartForm.closest('cart-drawer')) return;
          if (cartForm.enableAjaxUpdate) cartForm.refresh();
        });

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          detail: { opener: cartOpener || undefined, elementToFocus: pendingFocusItem || undefined }
        }));

        releaseBtn();
      })
      .catch(function (err) {
        if (typeof theme !== 'undefined') theme._pendingPostMergeFocus = false;
        releaseBtn();
        console.error('[upsell-atc] add to cart failed:', err);
        if (typeof onError === 'function') onError(err);
      });
  }

  /* ---------------------------------------------------------------
   * Size strip on product cards
   *   the "+" of a card with several variants toggles a row of size
   *   buttons over the bottom of the image (.variant-strip, rendered
   *   in snippets/product-block.liquid); picking one adds it.
   *
   * A non-modal disclosure, not a dialog: aria-expanded on the "+",
   * no focus trap. Tab walks from the "+" into the sizes and on out
   * of the card, which closes the strip. Escape closes it and puts
   * focus back on the "+".
   * --------------------------------------------------------------- */
  var openStripBtn = null;
  var stripCount = 0;

  function stripBlock(btn) {
    return btn.closest('.product-block__detail-cart-t');
  }

  function stripFor(btn) {
    var block = stripBlock(btn);
    return block ? block.querySelector('.variant-strip') : null;
  }

  function closeStrip(returnFocus) {
    var btn = openStripBtn;
    if (!btn) return;
    openStripBtn = null;
    var strip = stripFor(btn);
    if (strip) strip.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    var block = stripBlock(btn);
    if (block) block.classList.remove('is-strip-open');
    if (returnFocus && btn.isConnected) btn.focus();
  }

  function stripWraps(strip) {
    var items = strip.querySelectorAll('.variant-strip__options > li');
    return items.length > 1 && items[items.length - 1].offsetTop > items[0].offsetTop;
  }

  // Sizes that don't fit on one line would wrap into a ragged row (two
  // sizes on top, one below). Try smaller type first; if that still wraps,
  // stack them in a single column that grows up from the "+".
  function fitStrip(strip) {
    strip.classList.remove('variant-strip--compact', 'variant-strip--stacked');
    strip.style.maxHeight = '';
    if (!stripWraps(strip)) return;
    strip.classList.add('variant-strip--compact');
    if (!stripWraps(strip)) return;
    strip.classList.remove('variant-strip--compact');
    strip.classList.add('variant-strip--stacked');

    // Many sizes in a narrow card (or a high zoom level) could push the
    // column past the top of the image, where the card clips it. Cap it at
    // the image height; the column then scrolls (its buttons are focusable,
    // so keyboard users scroll it just by tabbing).
    var card = strip.closest('product-block');
    var image = card && card.querySelector('.image-cont');
    if (image && strip.offsetHeight > image.offsetHeight - 8) {
      strip.style.maxHeight = Math.max(image.offsetHeight - 8, 44) + 'px';
    }
  }

  function openStrip(btn, strip) {
    closeStrip(false);
    if (!strip.id) strip.id = 'VariantStrip-' + (++stripCount);
    btn.setAttribute('aria-controls', strip.id);
    strip.hidden = false;
    fitStrip(strip);
    btn.setAttribute('aria-expanded', 'true');
    stripBlock(btn).classList.add('is-strip-open');
    openStripBtn = btn;
  }

  // A failed add (size sold out since the page loaded, quantity limit...)
  // must not pass silently: say so in a role="alert" note on the card, where
  // the strip was. Created on first use, empty, and filled a frame later so
  // screen readers pick up the change.
  function showAddError(btn) {
    var block = btn && btn.isConnected ? stripBlock(btn) : null;
    if (!block) return;
    var note = block.querySelector('.product-block__add-error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'product-block__add-error';
      note.setAttribute('role', 'alert');
      block.appendChild(note);
    }
    note.textContent = '';
    requestAnimationFrame(function () {
      note.textContent = btn.dataset.errorText || '';
    });
  }

  function clearAddErrors() {
    document.querySelectorAll('.product-block__add-error').forEach(function (note) {
      note.textContent = '';
    });
  }

  function addFromCard(variantId, btn) {
    addToCart(variantId, btn, function () { showAddError(btn); });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openStripBtn) return;
    var block = stripBlock(openStripBtn);
    if (!block || !block.contains(document.activeElement)) return;
    e.preventDefault();
    closeStrip(true);
  });

  // Close once keyboard focus leaves the card. relatedTarget is null on a
  // plain mouse click (Safari doesn't focus buttons), which the click
  // handler below already deals with.
  document.addEventListener('focusout', function (e) {
    if (!openStripBtn) return;
    var block = stripBlock(openStripBtn);
    if (!block || !block.contains(e.target)) return;
    if (e.relatedTarget && !block.contains(e.relatedTarget)) closeStrip(false);
  });

  document.addEventListener('click', function (e) {
    clearAddErrors();

    // 1. "+" button on product card
    var btn = e.target.closest('.upsell-atc-btn');
    if (btn) {
      e.preventDefault();
      var strip = btn.hasAttribute('aria-expanded') ? stripFor(btn) : null;
      if (!strip) {
        addFromCard(btn.dataset.id, btn);
        return;
      }
      if (btn === openStripBtn) {
        closeStrip(false);
        return;
      }
      openStrip(btn, strip);
      return;
    }

    // 2. Size chosen → add it. Sold-out sizes stay in the strip (so the
    //    customer sees the size exists) but do nothing.
    var option = e.target.closest('.variant-strip__option');
    if (option) {
      if (option.getAttribute('aria-disabled') === 'true') return;
      // closeStrip clears openStripBtn, so grab the "+" first — it's the
      // button that shows the loading state.
      var opener = openStripBtn;
      var hadFocus = document.activeElement === option;
      closeStrip(false);
      // The option is about to be hidden; park keyboard focus on the "+"
      // so it isn't dropped to <body> while the add is in flight. The cart
      // drawer takes it from there on success.
      if (hadFocus && opener) opener.focus();
      addFromCard(option.dataset.variantId, opener);
      return;
    }

    // 3. Click anywhere outside the open strip → close it.
    if (openStripBtn && !e.target.closest('.variant-strip')) {
      closeStrip(false);
    }
  });

  /* ---------------------------------------------------------------
   * Tag-upsell picker dialog (second slot of the PDP upsell block)
   *   [data-upsell-picker-open="id"] → <dialog data-upsell-picker>
   *   option with one variant  → straight to the cart
   *   option with several      → swap to the variant panel, then add
   *
   * The dialog is a native <dialog> opened with showModal(), so Escape
   * and the inert background behind it are the browser's job — same
   * approach as the video gallery dialog in
   * snippets/product-video-gallery.liquid. Tab wrapping comes from the
   * "Diálogos modales: Tab en bucle" block below. All that is left here is
   * swapping panels and handing the variant id to addToCart above,
   * which already owns the cart refresh and the focus announcement.
   * --------------------------------------------------------------- */
  var PICKER_PANELS = ['list', 'variants'];

  function pickerPanel(dialog, name) {
    return dialog.querySelector('[data-upsell-panel="' + name + '"]');
  }

  function showPickerPanel(dialog, name) {
    PICKER_PANELS.forEach(function (panelName) {
      var panel = pickerPanel(dialog, panelName);
      if (panel) panel.hidden = panelName !== name;
    });
  }

  // The error region lives on the card, not in the dialog: by the time an add
  // can fail the dialog is already closed.
  function pickerErrorEl(dialog) {
    var root = dialog.closest('.product-tag-upsell');
    return root ? root.querySelector('[data-upsell-picker-error]') : null;
  }

  function clearPickerError(dialog) {
    var el = pickerErrorEl(dialog);
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  function closePicker(dialog, restoreFocus) {
    if (!dialog) return;
    dialog._upsellRestore = restoreFocus !== false;
    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  // Bound on first open rather than at load: the customiser can re-render the
  // section, and a re-rendered dialog is a different element.
  function bindPicker(dialog) {
    if (dialog._upsellBound) return;
    dialog._upsellBound = true;

    dialog.addEventListener('cancel', function (e) {
      // Take Escape over from the default close so the shared close handler
      // below runs the focus restore.
      e.preventDefault();
      closePicker(dialog);
    });

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('upsell-picker-open');
      showPickerPanel(dialog, 'list');
      var opener = dialog._upsellOpener;
      dialog._upsellOpener = null;
      if (opener) opener.setAttribute('aria-expanded', 'false');
      if (dialog._upsellRestore !== false && opener && opener.isConnected) {
        try { opener.focus(); } catch (_) { /* no-op */ }
      }
      dialog._upsellRestore = true;
    });
  }

  function openPicker(trigger) {
    var dialog = document.getElementById(trigger.getAttribute('data-upsell-picker-open'));
    if (!dialog) return;

    bindPicker(dialog);
    clearPickerError(dialog);
    dialog._upsellOpener = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    showPickerPanel(dialog, 'list');

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('upsell-picker-open');

    // showModal lands focus on the close button, which tells a screen reader
    // nothing about what just opened. The dialog itself carries the accessible
    // name (aria-labelledby), so focus that instead.
    requestAnimationFrame(function () {
      try { dialog.focus(); } catch (_) { /* no-op */ }
    });
  }

  function addFromPicker(dialog, variantId) {
    if (!variantId) return;
    var opener = dialog._upsellOpener;

    // Close before adding: the cart drawer is about to open and take focus,
    // and two stacked modals is the wrong answer for keyboard users. Focus
    // returns to the trigger in the meantime, so an add that fails leaves it
    // somewhere sensible instead of on <body>.
    closePicker(dialog);

    addToCart(variantId, opener, function () {
      var el = pickerErrorEl(dialog);
      if (!el) return;
      el.hidden = false;
      el.textContent = dialog.getAttribute('data-upsell-error-text') || '';
    });
  }

  function showVariantPanel(dialog, option, variants) {
    var titleEl = dialog.querySelector('[data-upsell-variants-title]');
    var listEl = dialog.querySelector('[data-upsell-variants-list]');
    if (!titleEl || !listEl) {
      addFromPicker(dialog, option.getAttribute('data-upsell-variant-id'));
      return;
    }

    var soldOutText = dialog.getAttribute('data-upsell-sold-out-text') || 'Sold out';
    titleEl.textContent = option.getAttribute('data-upsell-title') || '';
    listEl.textContent = '';

    variants.forEach(function (v) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'upsell-picker__variant';
      btn.setAttribute('data-upsell-variant', v.id);

      var name = document.createElement('span');
      name.className = 'upsell-picker__variant-name';
      name.textContent = v.title;

      // Sold-out rows keep the word visible, so availability never rests on
      // the strike-through alone.
      var value = document.createElement('span');
      value.className = 'upsell-picker__variant-value';
      value.textContent = v.available ? (v.price || '') : soldOutText;

      if (!v.available) btn.setAttribute('aria-disabled', 'true');

      btn.appendChild(name);
      btn.appendChild(value);
      li.appendChild(btn);
      listEl.appendChild(li);
    });

    showPickerPanel(dialog, 'variants');
    // Focus the heading so the chosen product is announced; the panel's Back
    // button is the previous tab stop from there.
    requestAnimationFrame(function () {
      try { titleEl.focus(); } catch (_) { /* no-op */ }
    });
  }

  function chooseOption(dialog, option) {
    var raw = option.getAttribute('data-upsell-variants');
    var variants = [];
    if (raw) {
      try { variants = JSON.parse(raw); } catch (_) { variants = []; }
    }

    if (!variants || variants.length <= 1) {
      addFromPicker(dialog, option.getAttribute('data-upsell-variant-id'));
      return;
    }
    showVariantPanel(dialog, option, variants);
  }

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-upsell-picker-open]');
    if (openBtn) {
      e.preventDefault();
      openPicker(openBtn);
      return;
    }

    // The picker card's image opens the dialog too. It deliberately does not
    // carry data-upsell-picker-open itself: openPicker records the trigger to
    // hand focus back to when the dialog closes and sets aria-expanded on it,
    // and a <div> can do neither. Resolving to the card's real button keeps
    // the image a pointer shortcut rather than a second control that keyboard
    // and screen reader users would have to tab past for no gain.
    var proxy = e.target.closest('[data-upsell-picker-proxy]');
    if (proxy) {
      var proxyCard = proxy.closest('.product-tag-upsell__card');
      var proxyBtn = proxyCard && proxyCard.querySelector('[data-upsell-picker-open]');
      if (proxyBtn) {
        e.preventDefault();
        openPicker(proxyBtn);
        return;
      }
    }

    var dialog = e.target.closest('[data-upsell-picker]');
    if (!dialog) return;

    // A click on the backdrop reports the dialog itself as the target.
    if (e.target === dialog) {
      closePicker(dialog);
      return;
    }

    if (e.target.closest('[data-upsell-picker-close]')) {
      closePicker(dialog);
      return;
    }

    if (e.target.closest('[data-upsell-picker-back]')) {
      showPickerPanel(dialog, 'list');
      var firstOption = dialog.querySelector('[data-upsell-option]');
      if (firstOption) {
        requestAnimationFrame(function () { firstOption.focus(); });
      }
      return;
    }

    var option = e.target.closest('[data-upsell-option]');
    if (option) {
      chooseOption(dialog, option);
      return;
    }

    var variantBtn = e.target.closest('[data-upsell-variant]');
    if (variantBtn) {
      if (variantBtn.getAttribute('aria-disabled') === 'true') return;
      addFromPicker(dialog, variantBtn.getAttribute('data-upsell-variant'));
    }
  });
})();


/* =================================================================
 * A11y: close the collection filter panel on Escape key
 *  - Theme toggles .filter-container--show-filters-desktop / -mobile
 *    on a <filter-container> when the user clicks [data-toggle-filters]
 *  - Pressing Esc should do the same dismiss action (mobile + desktop)
 * ================================================================= */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  var openContainer = document.querySelector(
    'filter-container.filter-container--show-filters-mobile, filter-container.filter-container--show-filters-desktop'
  );
  if (!openContainer) return;

  // Reuse the theme's existing toggle: triggering a click on a
  // [data-toggle-filters] button keeps the toggle state in sync with the
  // theme's internal flags (toggle-btn--revealed-desktop class, etc.).
  var toggleBtn = openContainer.querySelector('[data-toggle-filters]');
  if (toggleBtn) {
    toggleBtn.click();
  } else {
    openContainer.classList.remove(
      'filter-container--show-filters-mobile',
      'filter-container--show-filters-desktop'
    );
  }
});


/* =================================================================
 * A11y: filter panel — focus management + manual mobile focus trap
 *
 * Uses a MutationObserver on <filter-container> to react to class
 * changes (filter-container--show-filters-mobile / -desktop). More
 * robust than relying on the click handler order with the theme.
 *
 *  - On open: move focus into the panel (so Tab doesn't walk through
 *    sibling utility-bar icons first).
 *  - On mobile only: trap Tab inside the panel via a keydown listener
 *    that recomputes the focusable list at Tab time. Desktop keeps
 *    natural tab order — the sidebar is always visible there.
 *  - On close: restore focus to the toggle that opened it.
 * ================================================================= */
(function () {
  var FOCUSABLE_SEL =
    'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, ' +
    '[tabindex]:not([tabindex^="-"])';

  var trappedPanel = null;
  var filterTrapOpener = null;
  var lastToggleClicked = null;

  function getVisibleFocusables(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SEL))
      .filter(function (el) {
        return el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
      });
  }

  // Manual focus trap on Tab — only active when trappedPanel is set
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !trappedPanel) return;
    var focusables = getVisibleFocusables(trappedPanel);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    // If focus has somehow escaped the panel, pull it back.
    if (!trappedPanel.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Recapture focus if Tab walked it past first/last and out of the panel
  // (e.g. when first/last detection misses the user's last visible element).
  document.addEventListener('focusin', function (e) {
    if (!trappedPanel) return;
    if (trappedPanel.contains(e.target)) return;
    var focusables = getVisibleFocusables(trappedPanel);
    if (!focusables.length) return;
    var targetIsAfterPanel = trappedPanel.compareDocumentPosition(e.target)
      & Node.DOCUMENT_POSITION_FOLLOWING;
    if (targetIsAfterPanel) {
      focusables[0].focus();
    } else {
      focusables[focusables.length - 1].focus();
    }
  });

  // Track which toggle was clicked so we can return focus to it on close
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toggle-filters]');
    if (btn) lastToggleClicked = btn;
  });

  function handleStateChange(filterContainer) {
    var openMobile = filterContainer.classList.contains('filter-container--show-filters-mobile');
    var openDesktop = filterContainer.classList.contains('filter-container--show-filters-desktop');
    var panel = filterContainer.querySelector('.filters');

    if (openMobile || openDesktop) {
      // Just opened
      if (panel) {
        var firstFocusable = panel.querySelector('.filters__close') || panel.querySelector(FOCUSABLE_SEL);
        if (firstFocusable && !panel.contains(document.activeElement)) {
          firstFocusable.focus();
        }
      }
      // Trap only on mobile — the theme toggles both -mobile and -desktop classes
      // on every click, so the class alone isn't enough; gate on the actual viewport.
      var isMobileViewport = window.matchMedia('(max-width: 999.98px)').matches;
      if (openMobile && isMobileViewport && panel) {
        trappedPanel = panel;
        if (!filterTrapOpener) filterTrapOpener = lastToggleClicked;
      } else {
        trappedPanel = null;
      }
    } else {
      // Just closed
      trappedPanel = null;
      if (filterTrapOpener) {
        filterTrapOpener.focus();
        filterTrapOpener = null;
      }
    }
  }

  function attachObservers() {
    document.querySelectorAll('filter-container').forEach(function (fc) {
      if (fc.dataset.a11yObserverAttached === '1') return;
      fc.dataset.a11yObserverAttached = '1';
      var obs = new MutationObserver(function () {
        handleStateChange(fc);
      });
      obs.observe(fc, { attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObservers);
  } else {
    attachObservers();
  }
})();


/* =================================================================
 * A11y: close the mobile hamburger menu on Escape key
 *  - Theme toggles body.reveal-mobile-nav when user clicks .mobile-nav-toggle
 *  - Pressing Esc should trigger the same dismiss path so the focus
 *    trap and transition flags get cleaned up correctly
 * ================================================================= */
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  if (!document.body.classList.contains('reveal-mobile-nav')) return;
  // Con un <dialog> modal abierto encima (p. ej. región e idioma), Escape
  // cierra sólo ese diálogo y el menú sigue abierto debajo.
  if (document.querySelector('dialog[open]')) return;

  // Click the toggle button so the theme runs its full close flow
  // (removes reveal-mobile-nav, --revealed, enable-mobile-nav-transition,
  // releases the focus trap).
  var toggle = document.querySelector('.mobile-navigation-drawer .mobile-nav-toggle')
            || document.querySelector('.mobile-nav-toggle');
  if (toggle) toggle.click();
});


/* =================================================================
 * Drawer en escritorio: submenús en acordeón
 *  - En móvil el segundo nivel entra como panel lateral con botón
 *    "atrás". A partir de 768px el drawer es la navegación principal,
 *    así que el submenú se despliega hacia abajo dentro del panel y el
 *    chevron abre/cierra (patrón disclosure).
 *  - Se intercepta en fase de captura para que el click no llegue al
 *    handler de main.js, que es el que monta el panel lateral.
 * ================================================================= */
(function () {
  var TIER1_TOGGLE = '.mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__children-toggle';
  var mq = window.matchMedia('(min-width: 768px)');

  function setExpanded(item, toggle, isOpen) {
    item.classList.toggle('navigation__item--open', isOpen);
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    var link = toggle.previousElementSibling;
    if (link && link.hasAttribute('aria-expanded')) {
      link.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  function transitionMs(el) {
    return (parseFloat(getComputedStyle(el).transitionDuration) || 0) * 1000;
  }

  function openPanel(item, toggle, panel) {
    setExpanded(item, toggle, true);
    panel.style.height = panel.scrollHeight + 'px';
    // Altura automática al terminar, para que un tercer nivel pueda crecer
    // dentro sin quedar recortado por el overflow.
    setTimeout(function () {
      if (item.classList.contains('navigation__item--open')) panel.style.height = 'auto';
    }, transitionMs(panel));
  }

  function closePanel(item, toggle, panel) {
    panel.style.height = panel.scrollHeight + 'px';
    panel.offsetHeight; // reflow, si no la transición arranca desde 'auto'
    setExpanded(item, toggle, false);
    panel.style.height = '';
  }

  document.addEventListener('click', function (e) {
    if (!mq.matches || !e.target.closest) return;
    var toggle = e.target.closest(TIER1_TOGGLE);
    if (!toggle) return;
    e.preventDefault();
    e.stopPropagation();

    var item = toggle.parentElement;
    var panel = toggle.nextElementSibling;
    if (!panel) return;
    if (item.classList.contains('navigation__item--open')) closePanel(item, toggle, panel);
    else openPanel(item, toggle, panel);
  }, true);

  // Al cruzar el breakpoint hay que dejar los submenús cerrados: cada modo usa
  // un mecanismo distinto y mezclarlos deja el panel a medio montar.
  function resetOpenPanels() {
    document.querySelectorAll('.mobile-navigation-drawer .navigation__tier-1 > .navigation__item--open').forEach(function (item) {
      var toggle = item.querySelector(':scope > .navigation__children-toggle');
      var panel = item.querySelector(':scope > .navigation__tier-2-container');
      if (toggle) setExpanded(item, toggle, false);
      if (panel) {
        panel.style.height = '';
        panel.style.top = '';
      }
    });
    var drawer = document.querySelector('.mobile-navigation-drawer');
    if (drawer) drawer.classList.remove('mobile-navigation-drawer--child-open');
  }

  if (mq.addEventListener) mq.addEventListener('change', resetOpenPanels);
  else if (mq.addListener) mq.addListener(resetOpenPanels);
})();


/* =================================================================
 * A11y: keep the hamburger's aria-expanded in sync with the drawer.
 *  - The button's aria-controls now names the drawer it really opens
 *    instead of #main-nav, the desktop nav, which the drawer mode
 *    hides. main.js looks the button up by the old value, so it no
 *    longer maintains the state and we own it here.
 *  - Tracking body.reveal-mobile-nav covers every path at once: the
 *    toggle, Escape, and the page shade, which main.js closed without
 *    ever resetting aria-expanded (4.1.2). Replaces the earlier patch
 *    that only listened for the shade click.
 * ================================================================= */
(function () {
  var TOGGLE = '.mobile-nav-toggle[aria-controls="mobile-nav-drawer"]';

  function syncExpanded() {
    var isOpen = document.body.classList.contains('reveal-mobile-nav');
    document.querySelectorAll(TOGGLE).forEach(function (btn) {
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  new MutationObserver(syncExpanded).observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });
  syncExpanded();
})();

/* =================================================================
 * A11y: anunciar el drawer al abrirlo
 *  - El diálogo está bien expuesto (role="dialog" aria-modal="true"),
 *    pero main.js lleva el foco al botón de cerrar. VoiceOver lee
 *    entonces "Cerrar, botón" y no llega a nombrar el diálogo, así que
 *    el usuario no se entera de que se ha abierto un menú (4.1.2).
 *  - Movemos el foco al propio diálogo, que es el patrón de ARIA APG:
 *    con nombre accesible, el lector anuncia "Menú, cuadro de diálogo"
 *    y a partir de ahí el usuario navega el contenido.
 *  - Se engancha al primer focusin posterior a la apertura en lugar de
 *    a un temporizador, para no depender del retardo de 300 ms que usa
 *    main.js antes de mover el foco.
 * ================================================================= */
(function () {
  var TOGGLE = '.mobile-nav-toggle[aria-controls="mobile-nav-drawer"]';
  var abriendo = false;
  var reset;

  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest(TOGGLE)) return;
    // Si ya hay transición en curso, este clic es el de cerrar.
    if (document.body.classList.contains('enable-mobile-nav-transition')) return;
    abriendo = true;
    clearTimeout(reset);
    reset = setTimeout(function () { abriendo = false; }, 2000);
  }, true);

  document.addEventListener('focusin', function (e) {
    if (!abriendo) return;
    var drawer = document.querySelector('.mobile-navigation-drawer');
    if (!drawer || !drawer.contains(e.target)) return;
    abriendo = false;
    clearTimeout(reset);
    drawer.focus();
  });
})();


/* =================================================================
 * Drawer: pop-up de región e idioma
 *  - [data-localization-dialog-open="id"] en el pie del drawer abre el
 *    <dialog> de sections/header.liquid. El botón vive en la plantilla
 *    del drawer, y theme.suffixIds renombra sus aria-controls, por eso
 *    el destino va en un data-*.
 *  - showModal(): Escape y fondo inerte los pone el navegador; que el Tab
 *    dé la vuelta dentro, el bloque "Diálogos modales: Tab en bucle" de
 *    abajo. Aquí sólo queda llevar el foco al propio diálogo para que se
 *    anuncie su nombre, sincronizar aria-expanded y devolver el foco al
 *    botón al cerrar.
 *  - El drawer también atrapa el foco (trapFocus de main.js), pero sólo
 *    actúa sobre sus propios elementos, así que no choca con el diálogo.
 * ================================================================= */
(function () {
  function bind(dialog) {
    if (dialog._lgcLocalizationBound) return;
    dialog._lgcLocalizationBound = true;

    dialog.addEventListener('close', function () {
      var opener = dialog._lgcOpener;
      dialog._lgcOpener = null;
      if (!opener) return;
      opener.setAttribute('aria-expanded', 'false');
      if (opener.isConnected) {
        try { opener.focus(); } catch (_) { /* no-op */ }
      }
    });

    // Clic en el fondo oscuro: el propio <dialog> sólo recibe el clic ahí,
    // porque el contenido lo cubre entero.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
  }

  function open(trigger) {
    var dialog = document.getElementById(trigger.getAttribute('data-localization-dialog-open'));
    if (!dialog || typeof dialog.showModal !== 'function') return;

    bind(dialog);
    dialog._lgcOpener = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();

    // showModal deja el foco en el primer control; el diálogo lleva el nombre
    // (aria-labelledby), así que el foco va a él para que se anuncie.
    requestAnimationFrame(function () {
      try { dialog.focus(); } catch (_) { /* no-op */ }
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var trigger = e.target.closest('[data-localization-dialog-open]');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }

    var closeBtn = e.target.closest('[data-localization-dialog-close]');
    if (closeBtn) {
      var dialog = closeBtn.closest('dialog');
      if (dialog && dialog.open) dialog.close();
    }
  });
})();


/* =================================================================
 * Diálogos modales: Tab en bucle
 *  - showModal() deja inerte el resto de la página, pero al pasar del
 *    último control el navegador lleva el foco a su propia interfaz (la
 *    barra de URL) antes de volver. Afecta a todos los <dialog> modales:
 *    región e idioma (header), selector del upsell y vídeo de producto.
 *  - En el primer Tab dentro de un diálogo modal se le añaden dos
 *    centinelas invisibles, al principio y al final. Llegar al del final
 *    manda el foco al primer control; al del principio, al último. Así
 *    funciona también con los controles nativos del <video>, que viven
 *    dentro del reproductor y no se pueden enumerar desde fuera.
 *  - Con el foco en el propio diálogo (lo ponen ahí al abrir para que se
 *    anuncie su nombre), Tab va al primer control y Shift+Tab al último.
 * ================================================================= */
(function () {
  var FOCUSABLE = 'button, [href], input, select, textarea, video[controls], audio[controls], [tabindex]:not([tabindex="-1"])';

  function isModal(dialog) {
    try { return dialog.matches(':modal'); } catch (_) { return dialog.open; }
  }

  function focusables(dialog) {
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return !el.hasAttribute('data-dialog-sentinel') && !el.disabled && el.getClientRects().length > 0;
    });
  }

  function sentinel(edge) {
    var el = document.createElement('span');
    el.className = 'visually-hidden';
    el.tabIndex = 0;
    el.setAttribute('data-dialog-sentinel', edge);
    return el;
  }

  function ensureSentinels(dialog) {
    if (dialog.firstElementChild && dialog.firstElementChild.hasAttribute('data-dialog-sentinel')) return;
    dialog.insertBefore(sentinel('start'), dialog.firstChild);
    dialog.appendChild(sentinel('end'));
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var dialog = document.activeElement && document.activeElement.closest
      ? document.activeElement.closest('dialog')
      : null;
    if (!dialog || !isModal(dialog)) return;

    ensureSentinels(dialog);

    if (document.activeElement === dialog) {
      var items = focusables(dialog);
      if (!items.length) return;
      e.preventDefault();
      items[e.shiftKey ? items.length - 1 : 0].focus();
    }
  }, true);

  document.addEventListener('focusin', function (e) {
    var edge = e.target.getAttribute && e.target.getAttribute('data-dialog-sentinel');
    if (!edge) return;
    var items = focusables(e.target.closest('dialog'));
    if (!items.length) return;
    items[edge === 'end' ? 0 : items.length - 1].focus();
  });
})();


/* =================================================================
 * Klaviyo popup accessibility shim
 * - Wires aria-labelledby on the dialog to the form's first heading
 *   on every open (otherwise SR announces "Form dialog" with no
 *   context — auditor finding 4.1.2 / 1.3.1).
 * - Restores focus to the element that had it before the popup
 *   appeared, so dismissing the popup returns the user to where
 *   they were instead of <body> (auditor finding 2.4.3).
 * Fires on Klaviyo's own `klaviyoForms` event on `window`, so it
 * runs at most twice per popup lifecycle (open + close).
 * ================================================================= */
(function () {
  var klOpener = null;

  window.addEventListener('klaviyoForms', function (e) {
    var detail = e.detail || {};

    if (detail.type === 'open') {
      klOpener = document.activeElement;

      requestAnimationFrame(function () {
        var dialog = document.querySelector('[role="dialog"][aria-modal="true"][class*="kl-private"]')
                  || document.querySelector('[role="dialog"][aria-modal="true"]:not(:is(csm-cookie-consent *))');
        if (!dialog) return;
        var heading = dialog.querySelector('h1, h2, h3, [role="heading"]');
        if (!heading) return;
        if (!heading.id) heading.id = 'kl-form-title-' + (detail.formId || 'x');
        dialog.setAttribute('aria-labelledby', heading.id);
      });
    }

    if (detail.type === 'close') {
      if (klOpener && document.contains(klOpener) && typeof klOpener.focus === 'function') {
        klOpener.focus();
      }
      klOpener = null;
    }
  });
})();


/* =================================================================
 * Gold / Silver quick filter (snippets/metal-filter.liquid)
 * - Each pill ticks the matching checkbox in the filter drawer form
 *   and fires its change event, so the theme's own AJAX filtering
 *   (or plain form submit) runs exactly as if the drawer was used.
 * - After the theme swaps in the filtered results, the new product
 *   count is copied into a status region that stays in the page, so
 *   screen readers hear it. The count inside the AJAX container is
 *   replaced wholesale, and a freshly inserted live region is not
 *   reliably announced.
 * ================================================================= */
(function () {
  document.addEventListener('click', function (e) {
    var pill = e.target.closest('.metal-filter__swatch');
    if (!pill || pill.disabled) return;
    var form = document.getElementById('CollectionFilterForm');
    if (!form) return;
    var input = Array.prototype.find.call(form.querySelectorAll('input[type="checkbox"]'), function (el) {
      return el.value === pill.dataset.filterValue;
    });
    if (!input) return;

    var pressed = pill.getAttribute('aria-pressed') !== 'true';
    pill.setAttribute('aria-pressed', String(pressed));
    input.checked = pressed;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function watchCount(wrapper) {
    var status = wrapper.parentElement.querySelector('[data-metal-filter-status]');
    if (!status) return;
    new MutationObserver(function () {
      var count = wrapper.querySelector('[data-metal-filter-count]');
      if (count) status.textContent = count.textContent;
    }).observe(wrapper, { childList: true });
  }

  document.querySelectorAll('[data-metal-filter]').forEach(watchCount);
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-metal-filter]').forEach(watchCount);
  });
})();
